import { Skeleton, cn } from '@parcel/ui';

// Placeholders shaped like the detail sections they stand in for, so nothing jumps when data lands.

const keys = (prefix: string, count: number) =>
  Array.from({ length: count }, (_, i) => `${prefix}-${i}`);

// Varied widths read as text rather than a grid of bars.
const VALUE_WIDTHS = ['w-24', 'w-32', 'w-20', 'w-28', 'w-36', 'w-16'];

/** The identity row: avatar, title, meta line and badges. */
export function DetailIdentitySkeleton() {
  return (
    <div data-skeleton="identity" className="flex w-full items-start gap-4">
      <Skeleton className="h-12 w-12 shrink-0 rounded-full" />
      <div className="min-w-0 flex-1 space-y-2 pt-0.5">
        <Skeleton className="h-6 w-64 max-w-full" />
        <Skeleton className="h-4 w-48 max-w-full" />
        <div className="flex gap-1.5 pt-0.5">
          <Skeleton className="h-5 w-20 rounded-full" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
      </div>
    </div>
  );
}

/** Label/value rows matching DetailFieldGrid. */
export function FieldRowsSkeleton({ rows = 4, columns = 2 }: { rows?: number; columns?: 1 | 2 }) {
  return (
    <div className={cn('grid gap-x-8 gap-y-3.5', columns === 1 ? 'grid-cols-1' : 'grid-cols-2')}>
      {keys('row', rows).map((key, i) => (
        <div key={key} className="grid grid-cols-[7.5rem_minmax(0,1fr)] items-center gap-3">
          <Skeleton className="h-3.5 w-20" />
          <Skeleton className={cn('h-3.5', VALUE_WIDTHS[i % VALUE_WIDTHS.length])} />
        </div>
      ))}
    </div>
  );
}

/** A titled section of rows, as PropertySection. */
export function SectionSkeleton({ rows = 4, columns = 2 }: { rows?: number; columns?: 1 | 2 }) {
  return (
    <div data-skeleton="section" className="space-y-3">
      <Skeleton className="h-4 w-24" />
      <FieldRowsSkeleton rows={rows} columns={columns} />
    </div>
  );
}

/** A paragraph under a heading, as Description and About. */
export function TextSkeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div data-skeleton="text" className="space-y-2.5">
      <Skeleton className="h-4 w-24" />
      {keys('line', lines).map((key, i) => (
        <Skeleton key={key} className={cn('h-3.5', i === lines - 1 ? 'w-2/3' : 'w-full')} />
      ))}
    </div>
  );
}

/** Related records: avatar, name and subtitle per row; `plain` matches the borderless documents list. */
export function ListSkeleton({ rows = 4, plain = false }: { rows?: number; plain?: boolean }) {
  return (
    <ul data-skeleton="list" className={plain ? '-mx-2' : 'divide-y rounded-md border'}>
      {keys('item', rows).map((key, i) => (
        <li key={key} className={cn('flex items-center gap-3', plain ? 'px-2 py-2' : 'px-4 py-3')}>
          <Skeleton
            className={cn('shrink-0', plain ? 'h-9 w-9 rounded-md' : 'h-8 w-8 rounded-full')}
          />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className={cn('h-3.5', i % 2 ? 'w-40' : 'w-56')} />
            <Skeleton className="h-3 w-28" />
          </div>
          {plain ? null : <Skeleton className="h-5 w-20 shrink-0 rounded-full" />}
        </li>
      ))}
    </ul>
  );
}

/** The project cover at its final height. */
export function CoverSkeleton({ size }: { size: 'compact' | 'large' }) {
  return (
    <Skeleton
      data-skeleton="cover"
      className={cn('w-full rounded-lg', size === 'large' ? 'h-72' : 'h-48')}
    />
  );
}
